/**
 * Eventi per Google Tag Manager, nel dataLayer.
 *
 * Il sito e' una pagina sola: Tag Manager da solo vedrebbe una visita e
 * basta. Qui si mandano gli eventi che raccontano il funnel, con nomi e
 * parametri fissi documentati in docs/TRACCIAMENTO.md, cosi' il marketing
 * ci aggancia i tag di Analytics, Ads e Meta senza toccare il codice.
 *
 * Regola fissa: NIENTE dati personali. Ne' email, ne' nomi di file, ne'
 * identificativi di lavori. Solo conteggi, importi, nomi di piano e pagine.
 * Ogni funzione costruisce il suo oggetto campo per campo, di proposito.
 *
 * Gli eventi partono anche senza consenso: sono dati nel dataLayer, non
 * cookie. E' Tag Manager, con il Consent Mode, a decidere quali tag
 * possono usarli.
 */

const finestra = () => (typeof window !== "undefined" ? window : null);

function dataLayer(w = finestra()) {
  if (!w) return null;
  w.dataLayer = w.dataLayer || [];
  return w.dataLayer;
}

function numero(v) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : undefined;
}

function intero(v) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.round(n)) : undefined;
}

function pulito(dati) {
  const out = {};
  for (const [k, v] of Object.entries(dati)) {
    if (v === undefined || v === null || v === "") continue;
    out[k] = v;
  }
  return out;
}

/** Evento generico: usare le funzioni sotto, che sanno cosa mandare. */
export function evento(nome, dati = {}, w = finestra()) {
  const dl = dataLayer(w);
  if (!dl) return null;
  const voce = { event: nome, ...pulito(dati) };
  dl.push(voce);
  return voce;
}

/** Una volta sola per chiave, anche dopo un aggiornamento della pagina. */
function unaVolta(chiave, w = finestra()) {
  try {
    const k = `obscura_tracciato_${chiave}`;
    if (w.sessionStorage.getItem(k)) return false;
    w.sessionStorage.setItem(k, "1");
    return true;
  } catch (_) {
    return true;
  }
}

export const traccia = {
  /** Ogni cambio di pagina interno (il sito non ricarica mai). */
  paginaVista: (pagina, w) => evento("pagina_vista", {
    pagina: pagina || "landing",
    percorso: pagina && pagina !== "landing" ? `/?page=${pagina}` : "/",
  }, w),

  anonimizzazioneAvviata: ({ documenti, pagine, abbonato } = {}, w) => evento("anonimizzazione_avviata", {
    documenti: intero(documenti), pagine: intero(pagine), abbonato: Boolean(abbonato),
  }, w),

  anonimizzazioneCompletata: ({ documenti, abbonato } = {}, w) => evento("anonimizzazione_completata", {
    documenti: intero(documenti), abbonato: Boolean(abbonato),
  }, w),

  /** Flusso a consumo: il cliente apre il pagamento Stripe. */
  checkoutAperto: ({ pagine, importo } = {}, w) => evento("checkout_aperto", {
    pagine: intero(pagine), valore: numero(importo), valuta: "EUR",
  }, w),

  /** Flusso a consumo: pagamento riuscito (una volta per sessione Stripe). */
  pagamentoRiuscito: ({ sessione, importo, pagine } = {}, w = finestra()) => {
    if (!w || (sessione && !unaVolta(`pagamento_${sessione}`, w))) return null;
    return evento("pagamento_riuscito", {
      valore: numero(importo), valuta: "EUR", pagine: intero(pagine), transazione: sessione || undefined,
    }, w);
  },

  registrazione: (w) => evento("registrazione", {}, w),
  registrazioneConfermata: (w) => evento("registrazione_confermata", {}, w),
  accesso: (w) => evento("accesso", {}, w),

  /** Abbonamento: apertura del pagamento Stripe. */
  abbonamentoCheckout: ({ piano, cadenza, importo } = {}, w) => evento("abbonamento_checkout", {
    piano, cadenza, valore: numero(importo), valuta: "EUR",
  }, w),

  /** Abbonamento attivo dopo il pagamento (una volta per periodo). */
  abbonamentoAttivato: ({ piano, cadenza, importo, chiave } = {}, w = finestra()) => {
    if (!w || (chiave && !unaVolta(`abbonamento_${chiave}`, w))) return null;
    return evento("abbonamento_attivato", {
      piano, cadenza, valore: numero(importo), valuta: "EUR",
    }, w);
  },
};
